<template>
  <div class="w-full flex justify-center gap-2">
    <UseExample :src="exampleSrc" />
    <ScrollArea class="glass-frosted relative rounded-8xl w-250 h-67.5 p-4">
      <div class="inline-block">
        <div class="flex flex-wrap gap-1.5 p-2">
          <Button
            v-for="key in mediaKeys"
            :key="key.keywords"
            color="normal"
            class="h-10"
            draggable="true"
            @click="() => handleClick(key)"
            @dragstart="(event: DragEvent) => handleDragStart(key, event)"
          >
            <Icon v-if="key.icon" :icon="key.icon" />
            {{ key.labelKey ? $t(key.labelKey) : key.keywords }}
          </Button>
        </div>
      </div>
    </ScrollArea>
  </div>
</template>

<script setup lang="ts">
  import { inject, computed } from 'vue'
  import { storeToRefs } from 'pinia'
  import { setDragData, DragType } from '@/utils/dnd'
  import { Button, Icon, ScrollArea, toast } from '@rapoo/element'
  import { useDeviceStore, useLocaleStore } from '@/stores'
  import UseExample from '@/components/UseExample.vue'
  import { useDragImage } from '../composables/useDragImage'
  import { SelectedKeySymbol, UpdateSelectedKeyMappingSymbol } from '../types'
  import { KEYCODE_KEYWORDS_MAP } from '../utils/keyMapSettings'
  import { LEGACY_MEDIA_KEY_KEYWORDS, SPECIAL_KEY_META_BY_KEYWORDS } from '../utils/specialKeyMeta'
  import { DISABLED_KEYWORDS } from '@/utils/platformDisable'
  import useExampleZhCN from '@/assets/images/recording/keyboard/media-zhCN.gif'
  import useExampleEnUS from '@/assets/images/recording/keyboard/media-enUS.gif'
  import type { KeysCapability } from '@rapoo/device-sdk'
  import type { LocalizedSpecialKeyMapItem } from '../types'

  import type { KeyChangePayload, UpdateSelectedKeyMapping } from '../types'

  const selectedKey = inject(SelectedKeySymbol)
  const updateSelectedKeyMapping = inject<UpdateSelectedKeyMapping>(UpdateSelectedKeyMappingSymbol)

  const { language } = useLocaleStore()
  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  const exampleSrc = computed(() => (language === 'zh-CN' ? useExampleZhCN : useExampleEnUS))

  const deviceMediaKeywords = computed(() => {
    const cap = currentDevice.value?.manifest.capabilities?.find(
      (c): c is KeysCapability => c.type === 'keys',
    )
    return cap?.config.mediaKeys
  })

  /** 有机型白名单则按配置顺序；否则回退旧版多媒体列表 */
  const mediaKeys = computed(() => {
    const whitelist = deviceMediaKeywords.value
    const keywords = whitelist?.length ? whitelist : [...LEGACY_MEDIA_KEY_KEYWORDS]

    return keywords
      .map((kw) => SPECIAL_KEY_META_BY_KEYWORDS.get(kw))
      .filter((k): k is LocalizedSpecialKeyMapItem => k?.keyType === 'media')
      .filter((k) => !DISABLED_KEYWORDS.has(k.keywords))
  })

  const handleClick = (key: LocalizedSpecialKeyMapItem) => {
    if (selectedKey && selectedKey.value && updateSelectedKeyMapping) {
      const target = KEYCODE_KEYWORDS_MAP.get(key.keywords)

      const payload: KeyChangePayload = {
        keywords: selectedKey.value.keywords,
        keyType: 'media',
        target: target!,
        raw: {
          ...target,
          icon: key.icon,
        },
      }

      updateSelectedKeyMapping(payload)
      selectedKey.value = null
      toast.dismiss('keyboard')
    }
  }

  const { setDragImage } = useDragImage()

  const handleDragStart = (key: LocalizedSpecialKeyMapItem, event: DragEvent) => {
    const dataTransfer = event.dataTransfer
    if (!dataTransfer) return

    const target = KEYCODE_KEYWORDS_MAP.get(key.keywords)
    const dragData: KeyChangePayload = {
      keywords: '',
      keyType: 'media',
      target: target!,
      raw: {
        ...target,
        icon: key.icon,
      },
    }

    setDragData(dataTransfer, DragType.KEY_MAPPING, dragData)
    setDragImage(event, {
      icon: key.icon,
    })
  }
</script>

<style scoped></style>
